/**
 * Time helpers shared by the ticket engine (server) and the UI. Pure; every function takes the
 * instant as epoch milliseconds and an optional IANA time zone (default: the runtime's zone).
 */

export const MINUTE_MS = 60_000
export const HOUR_MS = 60 * MINUTE_MS
export const DAY_MS = 24 * HOUR_MS

/** Org time zone used for "6 pm that day" manual ETAs when nothing else is configured. */
export const DEFAULT_ORG_TIMEZONE = 'Asia/Kolkata'

export interface ZonedParts {
  year: number
  /** 1-12 */
  month: number
  day: number
  /** 0-23 */
  hour: number
  minute: number
}

const partsFormatters = new Map<string, Intl.DateTimeFormat>()

function partsFormatter(timeZone: string | undefined): Intl.DateTimeFormat {
  const key = timeZone ?? ''
  let f = partsFormatters.get(key)
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hourCycle: 'h23',
    })
    partsFormatters.set(key, f)
  }
  return f
}

/** Wall-clock parts of `ms` in `timeZone`. */
export function zonedParts(ms: number, timeZone?: string): ZonedParts {
  const out: Record<string, number> = {}
  for (const p of partsFormatter(timeZone).formatToParts(new Date(ms)))
    if (p.type !== 'literal') out[p.type] = Number(p.value)
  return {
    year: out.year ?? 1970,
    month: out.month ?? 1,
    day: out.day ?? 1,
    hour: (out.hour ?? 0) % 24,
    minute: out.minute ?? 0,
  }
}

/** Offset of `timeZone` from UTC at instant `ms`, in milliseconds (IST → +5.5h). */
function offsetAt(ms: number, timeZone: string): number {
  const p = zonedParts(ms, timeZone)
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute)
  return asUtc - Math.floor(ms / MINUTE_MS) * MINUTE_MS
}

/** The instant when the wall clock in `timeZone` reads the given local date and time. */
export function zonedTimeToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): number {
  const guess = Date.UTC(year, month - 1, day, hour, minute)
  let result = guess - offsetAt(guess, timeZone)
  // Second pass handles DST transitions between the guess and the result.
  const second = offsetAt(result, timeZone)
  result = guess - second
  return result
}

/** Parses `YYYY-MM-DD`; null when malformed or not a real date. */
export function parseIsoDate(value: string): { year: number; month: number; day: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!m) return null
  const year = Number(m[1])
  const month = Number(m[2])
  const day = Number(m[3])
  const d = new Date(Date.UTC(year, month - 1, day))
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day)
    return null
  return { year, month, day }
}

/** `YYYY-MM-DD` of `ms` in `timeZone` (for date inputs). */
export function toIsoDate(ms: number, timeZone?: string): string {
  const p = zonedParts(ms, timeZone)
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export interface FormatOptions {
  /** Reference "now" (decides whether the year is shown). Defaults to `Date.now()`. */
  now?: number
  timeZone?: string
}

/** "4 Nov", with the year appended when it isn't the current year ("4 Nov 2025"). */
export function fmtDate(ms: number, opts: FormatOptions = {}): string {
  const p = zonedParts(ms, opts.timeZone)
  const current = zonedParts(opts.now ?? Date.now(), opts.timeZone).year
  const base = `${p.day} ${MONTHS[p.month - 1]}`
  return p.year === current ? base : `${base} ${p.year}`
}

/** "3:05 pm" */
export function fmtTime(ms: number, opts: Pick<FormatOptions, 'timeZone'> = {}): string {
  const p = zonedParts(ms, opts.timeZone)
  const h = p.hour % 12 || 12
  return `${h}:${String(p.minute).padStart(2, '0')} ${p.hour < 12 ? 'am' : 'pm'}`
}

/** "4 Nov, 3:05 pm" */
export function fmtDateTime(ms: number, opts: FormatOptions = {}): string {
  return `${fmtDate(ms, opts)}, ${fmtTime(ms, opts)}`
}

/** "just now", "N min ago", "Nh ago", "Nd ago" (under 7 days), then the date. */
export function ago(ms: number, opts: FormatOptions = {}): string {
  const now = opts.now ?? Date.now()
  const diff = Math.max(0, now - ms)
  if (diff < MINUTE_MS) return 'just now'
  if (diff < HOUR_MS) return `${Math.floor(diff / MINUTE_MS)} min ago`
  if (diff < DAY_MS) return `${Math.floor(diff / HOUR_MS)}h ago`
  if (diff < 7 * DAY_MS) return `${Math.floor(diff / DAY_MS)}d ago`
  return fmtDate(ms, opts)
}

/** Paused durations in the activity log: "5h" under a day, otherwise "1.3d". */
export function fmtDuration(ms: number): string {
  const hours = ms / HOUR_MS
  if (hours < 24) return `${Math.max(1, Math.round(hours))}h`
  const days = Math.round((hours / 24) * 10) / 10
  return `${Number.isInteger(days) ? days.toFixed(0) : days.toFixed(1)}d`
}
